/**
 * InvoiceCard Component - Mobile-first card component for invoices
 * Displays invoice information in an action-first card format
 */

import React from "react";
import { Card, Typography, Button, Box, Chip, CircularProgress } from "@mui/material";
import { Send, Visibility, Download, Schedule } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import { logError } from "@/shared/utils/logger.js";
import { getInvoiceDisplayStatus } from "../utils/getInvoiceDisplayStatus.js";

const StyledCard = styled(Card)(({ theme, isOverdue, isDueSoon }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  cursor: "pointer",
  border: "1px solid transparent",
  borderLeft: isOverdue
    ? `4px solid ${theme.palette.error.main}`
    : isDueSoon
    ? `4px solid ${theme.palette.warning.main}`
    : "1px solid transparent",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-1px)",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 12,
});

const InvoiceIdText = styled(Typography)({
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
  fontFamily: "monospace",
});

const CustomerNameText = styled(Typography)({
  fontWeight: 600,
  fontSize: "1rem",
  marginBottom: 4,
});

const BalanceRow = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  marginBottom: 12,
});

const BalanceAmount = styled(Typography)(({ theme, isOverdue }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: isOverdue ? theme.palette.error.main : "rgba(0, 0, 0, 0.87)",
}));

const PaidAmount = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
}));

const InfoRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
  paddingTop: 12,
  borderTop: "1px solid rgba(0, 0, 0, 0.12)",
});

const ActionsRow = styled(Box)({
  display: "flex",
  gap: 8,
  marginTop: 12,
});

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch {
    return "Invalid date";
  }
};

const calculateUrgency = (dueDate, status) => {
  if (!dueDate || status === "paid" || status === "cancelled") {
    return { isOverdue: false, isDueSoon: false, daysUntilDue: null };
  }

  const now = new Date();
  const due = new Date(dueDate);
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return {
    isOverdue: diffDays < 0,
    isDueSoon: diffDays >= 0 && diffDays <= 3,
    daysUntilDue: diffDays,
  };
};

const getStatusAction = (status) => {
  switch (status) {
    case "draft":
      return { label: "Send Invoice", icon: Send, variant: "contained" };
    case "sent":
      return { label: "Remind", icon: Schedule, variant: "contained" };
    case "partial":
      return { label: "Follow Up", icon: Schedule, variant: "contained" };
    case "paid":
      return { label: "View", icon: Visibility, variant: "outlined" };
    case "overdue":
      return { label: "Remind", icon: Schedule, variant: "contained" };
    default:
      return { label: "View", icon: Visibility, variant: "outlined" };
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "draft":
      return "default";
    case "sent":
      return "info";
    case "paid":
      return "success";
    case "partial":
      return "warning";
    case "overdue":
      return "error";
    default:
      return "default";
  }
};

/**
 * InvoiceCard Component
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @param {boolean} props.isDownloadingPdf - Whether PDF is currently downloading
 * @param {Function} props.onViewInvoice - Callback when viewing invoice
 * @param {Function} props.onDownloadPdf - Callback when downloading PDF
 * @param {Function} props.onSendInvoice - Callback when sending invoice
 * @param {Function} props.onRemindInvoice - Callback when reminding about invoice
 * @param {Function} props.onFollowUpNavigate - Callback when navigating to follow-up
 */
const InvoiceCard = ({
  invoice,
  isDownloadingPdf = false,
  onViewInvoice,
  onDownloadPdf,
  onSendInvoice,
  onRemindInvoice,
  onFollowUpNavigate,
}) => {
  if (!invoice) return null;

  const invoiceId = invoice.id || invoice._id;
  const balance = invoice.paymentSummary?.balance || invoice.total || 0;
  const paid = invoice.paymentSummary?.totalPaid || 0;
  const total = invoice.total || 0;
  const status = getInvoiceDisplayStatus(invoice);
  const urgency = calculateUrgency(invoice.dueDate, status);
  const statusAction = getStatusAction(status);

  const handleCardClick = () => {
    if (onViewInvoice) {
      onViewInvoice(invoice);
    }
  };

  const handlePrimaryAction = (e) => {
    e.stopPropagation();
    try {
      if (status === "draft" && onSendInvoice) {
        onSendInvoice(invoice);
      } else if (
        (status === "sent" || status === "overdue") &&
        onRemindInvoice
      ) {
        onRemindInvoice(invoice);
      } else if (status === "partial" && onFollowUpNavigate) {
        onFollowUpNavigate(invoice);
      } else if (onViewInvoice) {
        onViewInvoice(invoice);
      }
    } catch (error) {
      logError("Failed to execute primary action", {
        invoiceId,
        status,
        error: error.message,
      });
    }
  };

  const handleView = (e) => {
    e.stopPropagation();
    if (onViewInvoice) {
      onViewInvoice(invoice);
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (onDownloadPdf) {
      onDownloadPdf(invoiceId);
    }
  };

  const handleSend = (e) => {
    e.stopPropagation();
    if (onSendInvoice) {
      onSendInvoice(invoice);
    }
  };

  const ActionIcon = statusAction.icon;

  return (
    <StyledCard
      onClick={handleCardClick}
      isOverdue={urgency.isOverdue}
      isDueSoon={urgency.isDueSoon}
    >
      <CardContent>
        <HeaderRow>
          <Box sx={{ flex: 1 }}>
            <InvoiceIdText variant="body2">{invoiceId}</InvoiceIdText>
            <CustomerNameText variant="body1">
              {invoice.customerName || invoice.customerPhone || "N/A"}
            </CustomerNameText>
          </Box>
          <Chip
            label={status}
            color={getStatusColor(status)}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        </HeaderRow>

        <BalanceRow>
          <Box>
            <BalanceAmount isOverdue={urgency.isOverdue}>
              {formatCurrency(balance)}
            </BalanceAmount>
            {paid > 0 && (
              <PaidAmount variant="body2">
                {formatCurrency(paid)} paid of {formatCurrency(total)}
              </PaidAmount>
            )}
          </Box>
          {urgency.isOverdue && (
            <Chip
              label="OVERDUE"
              size="small"
              sx={{
                backgroundColor: "error.main",
                color: "white",
                fontWeight: 600,
              }}
            />
          )}
        </BalanceRow>

        <InfoRow>
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              Due: {formatDate(invoice.dueDate)}
            </Typography>
            {urgency.isDueSoon && !urgency.isOverdue && (
              <Typography
                variant="caption"
                sx={{ color: "warning.main", fontWeight: 600 }}
              >
                Due in {urgency.daysUntilDue} day
                {urgency.daysUntilDue !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>
          {invoice?.followUpSummary && (
            <FollowUpMention
              summary={invoice.followUpSummary}
              onClick={(e) => {
                e.stopPropagation();
                if (onFollowUpNavigate) {
                  onFollowUpNavigate(invoice);
                }
              }}
            />
          )}
        </InfoRow>

        <ActionsRow>
          <Button
            variant={statusAction.variant}
            startIcon={<ActionIcon />}
            onClick={handlePrimaryAction}
            fullWidth
            sx={{
              flex: 1,
              minHeight: 44,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {statusAction.label}
          </Button>
          {status !== "sent" &&
            status !== "paid" &&
            status !== "cancelled" &&
            status !== "draft" &&
            onSendInvoice && (
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleSend}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                Send
              </Button>
            )}
          {statusAction.label !== "View" && (
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={handleView}
              sx={{
                minWidth: 44,
                minHeight: 44,
              }}
            >
              View
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={isDownloadingPdf ? <CircularProgress size={16} /> : <Download />}
            onClick={handleDownload}
            disabled={isDownloadingPdf}
            sx={{
              minWidth: 44,
              minHeight: 44,
            }}
          >
            PDF
          </Button>
        </ActionsRow>
      </CardContent>
    </StyledCard>
  );
};

export default InvoiceCard;

